import React, { useEffect, useState, useRef } from "react";
import "./MessageCard.css";
import ContactCard from "./ContactCard";
import FeatureOptions, { FeatureOption } from "./FeatureOptions";
import MarkdownCard from "./MarkdownCard";

export interface MessageProps {
  type: "user" | "assistant";
  content: string | React.ReactNode;
  contentType?:
  | "text"
  | "markdown"
  | "qrcode"
  | "combined"
  | "contact"
  | "featureOptions";
  isThinking?: boolean;
  isChunk?: boolean;
  typing?: boolean;
  onTypingComplete?: () => void;
  streamCompleted?: boolean;
  showCopyAndDownloadButtons?: boolean;
  contactOptions?: {
    onSubmit: (data: any) => void;
    onSkip: () => void;
    flow_type?: number;
    isFormShowed?: boolean;
  };
  featureOptions?: {
    onFeatureClick: (option: FeatureOption) => void;
    loading?: boolean;
  };
}

const MessageCard: React.FC<MessageProps> = ({
  type,
  content,
  contentType = "text",
  isThinking = false,
  typing = false,
  onTypingComplete,
  streamCompleted = false,
  showCopyAndDownloadButtons = false,
  contactOptions,
  featureOptions,
}) => {
  const [displayContent, setDisplayContent] = useState<string>("");
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const contentRef = useRef<string>("");
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const typingPositionRef = useRef<number>(0);
  const messageContentRef = useRef<HTMLDivElement>(null);

  // 处理打字效果
  useEffect(() => {
    if (isThinking) {
      return;
    }
    // 如果是联系人卡片或功能选项卡类型，不需要处理打字效果
    if (contentType === "contact" || contentType === "featureOptions") {
      return;
    }

    const contentStr = typeof content === "string" ? content : "";

    if (typing && contentStr && type === "assistant") {
      const prevContent = contentRef.current;
      contentRef.current = contentStr;

      // 如果新内容是之前内容的子集，继续打字
      if (contentStr.startsWith(prevContent) && contentStr !== prevContent) {
        setIsTyping(true);
        typingPositionRef.current = prevContent.length;
        setDisplayContent(prevContent);
      } else if (contentStr !== prevContent) {
        // 否则，重新开始打字
        setIsTyping(true);
        typingPositionRef.current = 0;
        setDisplayContent("");
      }
    } else {
      // 不使用打字效果时，直接显示完整内容
      setIsTyping(false);
      setDisplayContent(contentStr);
      contentRef.current = contentStr;

      if (onTypingComplete) {
        onTypingComplete();
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [
    content,
    typing,
    isThinking,
    type,
    onTypingComplete,
    streamCompleted,
    contentType,
  ]);

  // 处理打字动画
  useEffect(() => {
    if (isTyping && type === "assistant") {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      const step = 3; // 每次打字步长
      const interval = 100; // 打字间隔时间（毫秒）

      intervalRef.current = setInterval(() => {
        const fullContent = contentRef.current;
        const nextPosition = Math.min(
          typingPositionRef.current + step,
          fullContent.length
        );
        const nextContent = fullContent.substring(0, nextPosition);

        setDisplayContent(nextContent);
        typingPositionRef.current = nextPosition;

        if (nextPosition >= fullContent.length) {
          // 打字完成
          setIsTyping(false);
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }

          if (onTypingComplete) {
            onTypingComplete();
          }
        }
      }, interval);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [isTyping, type, onTypingComplete, streamCompleted]);

  // 特殊处理联系人卡片类型
  if (contentType === "contact" && contactOptions) {
    return (
      <div className="message assistant-message">
        <ContactCard
          onSubmit={contactOptions.onSubmit}
          onSkip={contactOptions.onSkip}
          flow_type={contactOptions.flow_type}
        />
      </div>
    );
  }

  // 特殊处理功能选项卡类型
  if (contentType === "featureOptions" && featureOptions) {
    return (
      <div className="message assistant-message feature-options-message">
        <div
          className="feature-options-container"
          style={{ marginTop: "20px", width: "100%" }}
        >
          <FeatureOptions
            onFeatureClick={featureOptions.onFeatureClick}
            loading={featureOptions.loading || false}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`message ${type}-message ${isThinking ? "thinking-message" : ""
        }`}
    >
      <div
        className={`bubble ${type === "user" ? "bubble-end" : "bubble-start"} ${isTyping ? "typing" : ""
          } ${isThinking ? "bubble-thinking" : ""}`}
        style={isThinking ? { width: "auto" } : {}}
      >
        {isThinking ? (
          <div className="bubble-content bubble-loading">
            <div className="reply-loading-container">
              <div className="reply-loading" />
              正在思考中...
            </div>
          </div>
        ) : (
          <div
            className={`bubble-content ${contentType === "qrcode" || contentType === "combined"
              ? "bubble-content-combined"
              : ""
              }`}
            ref={messageContentRef}
            style={
              contentType === "combined"
                ? {
                  height: "auto",
                  maxHeight: "none",
                  overflow: "visible",
                  padding: "12px 16px",
                }
                : {}
            }
          >
            {contentType === "markdown" ? (
              <MarkdownCard
                content={
                  typeof displayContent === "string" ? displayContent : ""
                }
                showButtons={
                  !isThinking &&
                  contentType == "markdown" &&
                  type === "assistant" &&
                  showCopyAndDownloadButtons
                }
              />
            ) : contentType === "qrcode" ? (
              <div className="qrcode-wrapper">{content}</div>
            ) : contentType === "combined" ? (
              <div className="combined-message-wrapper">{content}</div>
            ) : (
              <>{displayContent}</>
            )}
            {isTyping && <span className="typing-cursor"></span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageCard;
